'use client';
import { DropdownField, Input, Button } from '@/components/ui';
import React, { useCallback, useEffect } from 'react';
import { PriceFilter } from './filters/PriceFilter';
import { BedroomFilter } from './filters/BedroomFilter';
import LocationFilterMobile from './filters/location/LocationFilter.Mobile';
import { useStore } from '@/providers/StoreProvider';
import { IconSearch } from '@repo/svgs';
import { useRouter } from 'next/navigation';
import { usePriceFilter } from './filters/hooks/usePriceFilter';

export function ListingsSearchBarMobile() {
  const [openPriceFilter, setOpenPriceFilter] = React.useState<boolean>(false);
  const [openLocationFilter, setOpenLocationFilter] = React.useState<boolean>(false);
  const { price, minOptions, maxOptions } = usePriceFilter();

  const fetchNeighborhoods = useStore(s => s.neighborhoods.fetchNeighborhoods);
  const selectionLabel = useStore(s => s.listingsFilters.getLocationSelectionLabel());
  const getUrlFilterPayload = useStore(s => s.listingsFilters.getUrlFilterPayload);
  const bedrooms = useStore(s => s.listingsFilters.bedrooms);
  const neighborhoodIds = useStore(s => s.listingsFilters.neighborhoodIds);
  const building = useStore(s => s.listingsFilters.building);
  const listingsCount = useStore(s => s.listingsFilters.listingsCount);
  const isCountLoading = useStore(s => s.listingsFilters.isCountLoading);
  const updateListingsCount = useStore(s => s.listingsFilters.updateListingsCount);
  const router = useRouter();

  React.useEffect(() => {
    fetchNeighborhoods();
  }, []);

  useEffect(() => {
    updateListingsCount({ withMapBoundaries: false });
  }, [price, bedrooms, neighborhoodIds, building]);

  const goToListings = useCallback(() => {
    const urlPayload = getUrlFilterPayload();
    router.push(`/listings/${urlPayload}`);
  }, [router, price, bedrooms, neighborhoodIds, building]);

  return (
    <div className="container-grid gap-4">
      <div className="col-span-full">
        <Input
          onClick={() => setOpenLocationFilter(true)}
          label="Location"
          readOnly
          placeholder="Search neighborhood, address, or building"
          value={selectionLabel || ''}
        />
      </div>
      <div className="col-span-2">
        <DropdownField
          label="Min price"
          selectedLabel={minOptions.find(o => o.value === price.min)?.label}
          onClick={() => setOpenPriceFilter(true)}
        />
      </div>
      <div className="col-span-2">
        <DropdownField
          label="Max price"
          selectedLabel={maxOptions.find(o => o.value === price.max)?.label}
          onClick={() => setOpenPriceFilter(true)}
        />
      </div>
      <div className="col-span-full">
        <BedroomFilter />
      </div>
      <div className="col-span-full">
        <Button
          className="w-full flex items-center justify-center gap-2"
          variant="primary"
          onClick={goToListings}
          isLoading={isCountLoading}
          disabled={isCountLoading || !listingsCount}
        >
          {!isCountLoading && <IconSearch className="w-4 h-4" />}
          <span className="text-body-2-high">
            {`Search ${listingsCount !== null ? listingsCount.toLocaleString() : ''} listings`}
          </span>
        </Button>
      </div>
      <PriceFilter
        variant="mobile"
        requireConfirmation
        shouldUpdateUrl={false}
        page="home"
        open={openPriceFilter}
        onClose={() => setOpenPriceFilter(false)}
      />
      <LocationFilterMobile
        open={openLocationFilter}
        onClose={() => setOpenLocationFilter(false)}
      />
    </div>
  );
}

export default ListingsSearchBarMobile;
